import { useEffect, useState } from 'react'
import { useAuthContext } from '../../../../context/auth.context'
import { listResources } from './api'
import { isKeyStaff } from './grants-access'
import { holdsPlatformOnly } from './grant-words'

// EducationXR's own organization, the platform organization (core's
// organizations.is_platform, one row), holds the platform keys (owner
// 2026-10-23: AI tools telemetry is platform data, so its keys are the
// platform's, not an organization's; called "platform keys" because a key's
// Phase E "System key" is its id space, another thing). Only there may a key
// be given a platform-only permission such as AI tools telemetry: core
// refuses it elsewhere (403 api_key.platform_only).
//
// No route names that organization, and the admin never hard-codes it, so
// this reads it from what content says of the organization the session is
// in: its resource list (GET /api-key/resources, EducationXR staff only)
// offers the platform-only groups there and nowhere else. A key that holds
// one (any viewer's own list) says the same.
export const offersPlatformOnly = groups => Array.isArray(groups) && groups.some(group => !!group && group.platform_only === true)

// The words for it.
export const PLATFORM_LABEL = 'Platform'
export const PLATFORM_KEYS_NOTE = "This is EducationXR's own organization: its keys are platform keys. Only a platform key can be given platform permissions, such as AI tools telemetry."

// { status, here, organizationID, organizationName, error }: status is
// 'loading' while staff's read runs, 'here' when the session's organization
// is the platform organization (organizationID is then its id), 'elsewhere'
// when it is not, and 'unknown' when it can't be said (not staff, or the
// read failed: error). `keys`, the organization's own keys when the page has
// them, also answer 'here' for someone who can't read the resources.
export const usePlatformOrganization = ({ keys } = {}) => {
  const context = useAuthContext()
  const state = context && context.state
  const staff = isKeyStaff(state)
  const organizationID = (state && (state.organizationID || (state.jwtClaims && state.jwtClaims.organizationID))) || null
  const organizationName = (state && (state.organizationName || (state.jwtClaims && state.jwtClaims.organizationName))) || ''
  const [answer, setAnswer] = useState(() => ({ status: staff && organizationID ? 'loading' : 'unknown' }))

  useEffect(() => {
    if (!staff || !organizationID) {
      setAnswer({ status: 'unknown' })
      return undefined
    }
    let live = true
    setAnswer({ status: 'loading' })
    Promise.resolve().then(listResources).then(
      groups => { if (live) setAnswer({ status: offersPlatformOnly(groups) ? 'here' : 'elsewhere' }) },
      err => { if (live) setAnswer({ status: 'unknown', error: err }) }
    )
    return () => { live = false }
  }, [staff, organizationID])

  const here = answer.status === 'here' || (Array.isArray(keys) && keys.some(holdsPlatformOnly))
  return {
    status: here ? 'here' : answer.status,
    here,
    organizationID: here && organizationID ? Number(organizationID) : null,
    organizationName,
    error: answer.error
  }
}
